"use client";

import React, { useState } from "react";
import { ComponentConfig } from "@puckeditor/core";
import { businessData } from "@/data/business";

export interface BatchTier {
  units: number;
  label: string;
  leadDays: string;
  sampling: string;
  qcLevel: string;
  packaging: string;
}

export interface BatchEstimatorProps {
  heading?: string;
  subheading?: string;
  tiers?: BatchTier[];
}

export const BatchEstimatorBlock: ComponentConfig<BatchEstimatorProps> = {
  label: "Batch Estimator Calculator",
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    tiers: {
      type: "array",
      getItemSummary: (tier: any) => tier.label || `${tier.units} Units`,
      arrayFields: {
        units: { type: "number", label: "Units count" },
        label: { type: "text", label: "Pill Label" },
        leadDays: { type: "text", label: "Lead Execution Time" },
        sampling: { type: "text", label: "Sampling Dispatch Time" },
        qcLevel: { type: "text", label: "QC Protocol" },
        packaging: { type: "text", label: "Packaging Compatibility" },
      },
      defaultItemProps: {
        units: 500,
        label: "500 Units (Pilot Batch)",
        leadDays: "14–18 days after approvals",
        sampling: "Target dispatch within 14 days",
        qcLevel: "Microbial, pH, viscosity, appearance",
        packaging: "Stock bottles, tubes, droppers, or airless packs",
      },
    },
  },
  defaultProps: {
    heading: "Production Lead-Time & Specification Estimator",
    subheading: "Select your target batch quantity to evaluate production lead-times, sampling turnarounds, QC protocols, and packaging compatibility.",
    tiers: businessData.batchEstimator.tiers,
  },
  render: function BatchEstimator({ heading, subheading, tiers }) {
    const tierList = tiers && tiers.length > 0 ? tiers : businessData.batchEstimator.tiers;
    const [selectedUnits, setSelectedUnits] = useState<number>(tierList[0]?.units || 500);

    const activeTier = tierList.find((t) => t.units === selectedUnits) || tierList[0];

    return (
      <section className="sec sec-alt" id="calculator">
        <div className="container">
          <div className="sec-head">
            <h2>{heading}</h2>
            <p className="sec-sub">{subheading}</p>
          </div>

          <div className="calc-module" data-animate>
            <div className="calc-selector-bar">
              {tierList.map((tier) => (
                <button
                  key={tier.units}
                  type="button"
                  className={`calc-pill ${selectedUnits === tier.units ? "active" : ""}`}
                  onClick={() => setSelectedUnits(tier.units)}
                >
                  {tier.label}
                </button>
              ))}
            </div>

            <div className="calc-grid">
              <div className="calc-item">
                <label>Estimated Batch Execution</label>
                <span className="calc-value">{activeTier?.leadDays || "Confirmed after brief"}</span>
              </div>
              <div className="calc-item">
                <label>R&D Sample Dispatch</label>
                <span className="calc-value">{activeTier?.sampling || "Within 14 Days"}</span>
              </div>
              <div className="calc-item">
                <label>Quality Control Protocol</label>
                <span className="calc-value">{activeTier?.qcLevel || "Standard Micro + pH + Viscosity"}</span>
              </div>
              <div className="calc-item">
                <label>Packaging Compatibility</label>
                <span className="calc-value">{activeTier?.packaging || "Stock Airless / Droppers / Bottles"}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  },
};
